import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

export type PopoverCloseReason = 'escape' | 'outside' | 'done';

export interface PopoverProps {
  anchor: HTMLElement | null;
  open: boolean;
  /** the owner decides what to do with half-built content: it is never thrown away by the popover itself (Q-D-19) */
  onClose: (reason: PopoverCloseReason) => void;
  children: ReactNode;
  label?: string;
  /** below the anchor (start / end aligned), to its right with the bottoms aligned (the rail's account menu), or above it */
  placement?: 'bottom-start' | 'bottom-end' | 'right-end' | 'top-start';
  className?: string;
}

/** An anchored popover: Escape and an outside click close it (the owner keeps the content), focus returns to the anchor. */
export function Popover({ anchor, open, onClose, children, label, placement = 'bottom-start', className = '' }: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open || !anchor) return;
    const r = anchor.getBoundingClientRect();
    const w = ref.current?.offsetWidth ?? 240;
    const h = ref.current?.offsetHeight ?? 200;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (placement === 'right-end') setPos({ top: Math.max(8, Math.min(r.bottom - h, vh - h - 8)), left: Math.min(r.right + 8, vw - w - 8) });
    else if (placement === 'top-start') setPos({ top: Math.max(8, r.top - h - 4), left: Math.max(8, Math.min(r.left, vw - w - 8)) });
    else if (placement === 'bottom-end') setPos({ top: r.bottom + 4, left: Math.max(8, r.right - w) });
    else setPos({ top: r.bottom + 4, left: Math.max(8, Math.min(r.left, vw - w - 8)) });
  }, [open, anchor, placement]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose('escape');
        anchor?.focus();
      }
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || anchor?.contains(t)) return;
      onClose('outside');
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [open, onClose, anchor]);

  useEffect(() => {
    if (open) {
      const first = ref.current?.querySelector<HTMLElement>('input, select, textarea, button, [tabindex]');
      first?.focus();
    }
  }, [open]);

  if (!open) return null;
  return (
    <div ref={ref} className={`popover ${className}`} role="dialog" aria-label={label} style={{ top: pos.top, left: pos.left }}>
      {children}
    </div>
  );
}

/** A small action menu (the grid's column menu, row menus): one button per item, closes after a pick. */
export function Menu({ anchor, open, onClose, items, label }: { anchor: HTMLElement | null; open: boolean; onClose: () => void; items: { key: string; label: ReactNode; icon?: ReactNode; onSelect?: () => void; disabled?: boolean; separator?: boolean; danger?: boolean }[]; label: string }) {
  const ref = useRef<HTMLUListElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  useLayoutEffect(() => {
    if (!open || !anchor) return;
    const r = anchor.getBoundingClientRect();
    const w = ref.current?.offsetWidth ?? 200;
    const h = ref.current?.offsetHeight ?? 200;
    const top = r.bottom + 2 + h > window.innerHeight - 8 ? Math.max(8, r.top - h - 2) : r.bottom + 2;
    setPos({ top, left: Math.max(8, Math.min(r.left, window.innerWidth - w - 8)) });
  }, [open, anchor]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        anchor?.focus();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node) && !anchor?.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown, true);
    ref.current?.querySelector<HTMLElement>('button:not(:disabled)')?.focus();
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [open, onClose, anchor]);
  if (!open) return null;
  return (
    <ul ref={ref} className="menu" role="menu" aria-label={label} style={{ top: pos.top, left: pos.left }}>
      {items.map((item) =>
        item.separator ? (
          <li key={item.key} className="menu-sep" role="separator" />
        ) : (
          <li key={item.key} role="none">
            <button
              type="button"
              role="menuitem"
              className={`menu-item ${item.danger ? 'error' : ''}`}
              disabled={item.disabled}
              onClick={() => {
                onClose();
                item.onSelect?.();
              }}
            >
              {item.icon}
              {item.label}
            </button>
          </li>
        ),
      )}
    </ul>
  );
}
